import { NextAuthOptions, Account, Profile, User } from 'next-auth';
import { JWT } from 'next-auth/jwt';
import KeycloakProvider from 'next-auth/providers/keycloak';

/**
 * Configuration interface for NextAuth SSO setup
 */
export interface SSOConfig {
  keycloakIssuer: string;
  clientId: string;
  clientSecret: string;
  nextAuthSecret: string;
  enableDebug?: boolean;
  enablePKCE?: boolean;
  customSignInPage?: string;
  tokenEndpointAuthMethod?: 'client_secret_basic' | 'client_secret_post' | 'none';
}

/**
 * Extended JWT token interface with SSO-specific properties
 */
export interface ExtendedJWT extends JWT {
  accessToken?: string;
  idToken?: string;
  refreshToken?: string;
  expiresAt?: number;
  error?: string;
  // Chipotle-specific fields from JWT
  username?: string;
  employeeId?: string;
  isStoreAccount?: boolean;
}

/**
 * Extended session interface with SSO-specific properties
 */
export interface ExtendedSession {
  user?: {
    name?: string | null;
    email?: string | null;
    image?: string | null;
    // Chipotle-specific user fields
    username?: string;
    employeeId?: string;
    isStoreAccount?: boolean;
  };
  accessToken?: string;
  idToken?: string;
  error?: string;
  expires: string;
}

/**
 * Create a standardized Keycloak provider configuration
 */
export const createKeycloakProvider = (config: SSOConfig) => {
  const baseConfig: any = {
    clientId: config.clientId,
    clientSecret: config.clientSecret,
    issuer: config.keycloakIssuer,
    idToken: true,
    httpOptions: {
      timeout: 10000, // Increase timeout to 10s to prevent OIDC discovery/token exchange timeouts
    },
  };

  if (config.tokenEndpointAuthMethod) {
    baseConfig.client = {
      token_endpoint_auth_method: config.tokenEndpointAuthMethod,
    };
  }

  if (config.enablePKCE !== false) {
    return KeycloakProvider({
      ...baseConfig,
      checks: ['pkce', 'state'],
      authorization: {
        params: {
          code_challenge_method: 'S256',
        },
      },
    } as any);
  }

  return KeycloakProvider(baseConfig);
};

/**
 * Create a standardized token refresh function
 */
export const createTokenRefreshFunction = (config: SSOConfig) => {
  return async (token: ExtendedJWT): Promise<ExtendedJWT> => {
    const currentTime = Math.floor(Date.now() / 1000);

    if (!token.refreshToken || typeof token.refreshToken !== 'string') {
      console.error('Invalid or missing refresh token');
      return { ...token, error: 'RefreshAccessTokenError' };
    }

    console.log('Access token expired. Attempting to refresh...');

    try {
      const isKeycloak = config.keycloakIssuer.includes('/realms/');
      const tokenEndpoint = isKeycloak
        ? `${config.keycloakIssuer}/protocol/openid-connect/token`
        : `${config.keycloakIssuer}/token`;

      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          client_id: config.clientId,
          client_secret: config.clientSecret,
          grant_type: 'refresh_token',
          refresh_token: token.refreshToken,
        }),
      });

      if (!response.ok) {
        const errorMessage = await response.text();
        console.error('Failed to refresh token:', errorMessage);
        throw new Error('Failed to refresh access token');
      }

      const refreshedTokens = await response.json();

      return {
        ...token,
        accessToken: refreshedTokens.access_token,
        idToken: refreshedTokens.id_token,
        expiresAt: currentTime + refreshedTokens.expires_in,
        refreshToken: refreshedTokens.refresh_token || token.refreshToken,
      };
    } catch (error) {
      console.error('Error refreshing token:', error);
      return { ...token, error: 'RefreshAccessTokenError' };
    }
  };
};

/**
 * Create standardized NextAuth callbacks
 */
export const createNextAuthCallbacks = (refreshAccessToken: (token: ExtendedJWT) => Promise<ExtendedJWT>) => {
  return {
    async jwt({ token, account, profile }: { token: ExtendedJWT; account: Account | null; profile?: Profile }) {
      const currentTime = Math.floor(Date.now() / 1000);
      
      if (account) {
        console.log('[NextAuth] JWT callback - New login, account:', {
          provider: account.provider,
          type: account.type,
          hasAccessToken: !!account.access_token,
          hasIdToken: !!account.id_token,
          hasRefreshToken: !!account.refresh_token,
        });
        console.log('[NextAuth] Profile:', profile);
        
        token.accessToken = account.access_token;
        token.idToken = account.id_token;
        token.refreshToken = account.refresh_token;
        token.expiresAt = currentTime + (Number(account.expires_in) || 60);

        // Extract user information from JWT token
        const userInfo = parseJWTUserInfo(account.id_token);
        token.username = userInfo.username;
        token.employeeId = userInfo.employeeId;
        token.isStoreAccount = userInfo.isStoreAccount;
      }

      if (token.expiresAt && Number(token.expiresAt) < currentTime) {
        return await refreshAccessToken(token);
      }

      return token;
    },

    async session({ session, token }: { session: any; token: ExtendedJWT }) {
      console.log('[NextAuth] Session callback - User:', session.user?.email, 'Username:', token.username);
      session.accessToken = token.accessToken as string;
      session.idToken = token.idToken as string;
      session.error = token.error as string;
      
      // Add Chipotle-specific user information to session
      if (session.user) {
        session.user.username = token.username;
        session.user.employeeId = token.employeeId;
        session.user.isStoreAccount = token.isStoreAccount;
      }
      
      return session;
    },
  };
};

/**
 * Create a complete NextAuth configuration
 */
export const createNextAuthConfig = (config: SSOConfig): NextAuthOptions => {
  const keycloak = createKeycloakProvider(config);
  const refreshAccessToken = createTokenRefreshFunction(config);
  const callbacks = createNextAuthCallbacks(refreshAccessToken);

  return {
    debug: config.enableDebug ?? true,
    secret: config.nextAuthSecret,
    providers: [keycloak],
    pages: config.customSignInPage ? {
      signIn: config.customSignInPage,
    } : undefined,
    callbacks,
  };
};

/**
 *
 *  Normalize URL to prevent double slashes
 */
const normalizeUrl = (baseUrl: string, path: string): string => {
  const cleanBase = baseUrl.replace(/\/$/, ''); // Remove trailing slash
  const cleanPath = path.startsWith('/') ? path : `/${path}`; // Ensure path starts with slash
  return `${cleanBase}${cleanPath}`;
};

/**
 * Parse JWT token to extract user information
 * Handles both standard employee accounts and store accounts
 */
export const parseJWTUserInfo = (idToken?: string): { username?: string; employeeId?: string; isStoreAccount?: boolean } => {
  if (!idToken) {
    return {};
  }

  try {
    // Decode JWT token (base64 decode the payload)
    const parts = idToken.split('.');
    if (parts.length !== 3) {
      console.warn('[JWT] Invalid token format');
      return {};
    }

    const payload = JSON.parse(atob(parts[1]));
    console.log('[JWT] Parsed token payload:', {
      sub: payload.sub,
      email: payload.email,
      // Don't log full payload for security
    });

    const username = payload.sub; // The 'sub' field contains the username
    
    if (!username) {
      console.warn('[JWT] No username found in token');
      return {};
    }

    // Determine if this is a store account or employee account
    // Store accounts: IL.2553.Cicero@chipotle.com
    // Employee accounts: 1478959@chipotle.com
    const isStoreAccount = username.includes('.') && !username.match(/^\d+@/);
    
    // Extract employee ID for standard accounts (everything before @)
    const employeeId = isStoreAccount ? undefined : username.split('@')[0];

    console.log('[JWT] User info extracted:', {
      username,
      employeeId,
      isStoreAccount,
    });

    return {
      username,
      employeeId,
      isStoreAccount,
    };
  } catch (error) {
    console.error('[JWT] Error parsing token:', error);
    return {};
  }
};

/**
 * Create a standardized logout API handler
 */
export const createLogoutApiHandler = () => {
  return async (
    req: any, 
    res: any, 
    getServerSession: (req: any, res: any, options: NextAuthOptions) => Promise<any>, 
    authOptions: NextAuthOptions
  ) => {
    if (req.method !== 'POST') {
      return res.status(405).json({ message: 'Method not allowed' });
    }

    try {
      const session = await getServerSession(req, res, authOptions);
      
      // Check if this is an SSO session (hybrid mode support)
      // In hybrid mode, SSO_ENABLED might be false but user could still have SSO session
      const hasSSO = !!(session?.idToken || session?.accessToken);
      
      if (!hasSSO) {
        // No SSO session detected, just return login page
        console.log('[Logout API] No SSO session detected, returning login page');
        const SITE_URL = process.env.SITE_URL || process.env.NEXTAUTH_URL || 'http://localhost:3000';
        // Ensure no double slashes by removing trailing slash from SITE_URL and adding /login
        const baseUrl = SITE_URL.replace(/\/$/, '');
        return res.status(200).json({ logoutUrl: `${baseUrl}/login` })
      }

      // User has SSO session, build Identity Provider logout URL
      console.log('[Logout API] SSO session detected, building Identity Provider logout URL');
      
      const KEYCLOAK_ISSUER = process.env.NEXT_PUBLIC_SSO_KEYCLOAK_ISSUER!;
      const CLIENT_ID = process.env.SSO_KEYCLOAK_CLIENT_ID!;
      const SITE_URL = process.env.SITE_URL || process.env.NEXTAUTH_URL || 'http://localhost:3000';
      
      const isKeycloak = KEYCLOAK_ISSUER.includes('/realms/');
      const logoutEndpoint = isKeycloak
        ? `${KEYCLOAK_ISSUER}/protocol/openid-connect/logout`
        : `${KEYCLOAK_ISSUER}/logout`;

      const logoutUrl = new URL(logoutEndpoint);
      
      // Add client_id to help Keycloak identify the client
      logoutUrl.searchParams.set('client_id', CLIENT_ID);
      
      // Add id_token_hint if available (helps with logout but not required)
      if (session?.idToken) {
        logoutUrl.searchParams.set('id_token_hint', session.idToken as string);
      }
      
      // Always set the post logout redirect URI
      logoutUrl.searchParams.set('post_logout_redirect_uri', `${SITE_URL}/login`);
      
      console.log('[Logout API] Built Keycloak logout URL:', logoutUrl.toString());
      
      return res.status(200).json({ logoutUrl: logoutUrl.toString() });
    } catch (error) {
      console.error('Logout API Error:', error);
      return res.status(500).json({ message: 'Internal server error' });
    }
  };
};